import { useTranslate } from '@jablotron/lib.spa-lang';
import { FOOTER_PARTNER_LINKS, type FooterContact, type FooterLink } from '../footer-data';
import { useFooterContact, useFooterUsefulInfoLinks } from '../use-footer-contact';
import { FooterAccordionSection } from './FooterAccordionSection';
import { useLocalisedHref } from '~/entities/urls/use-localised-href';
import { Texts } from '~/texts/runtime';

function getQuickContacts(contact: FooterContact) {
  return [
    { icon: '/images/footer/icon-envelope.svg', label: contact.email, href: `mailto:${contact.email}` },
    ...(contact.phone
      ? [
          {
            icon: '/images/footer/icon-telephone.svg',
            label: contact.phone,
            href: `tel:${contact.phone.replace(/\s/g, '')}`,
          },
        ]
      : []),
    ...(contact.website
      ? [{ icon: '/images/footer/icon-web.svg', label: contact.website, href: `https://${contact.website}/` }]
      : []),
  ];
}

function FooterLinkList({ links }: { links: FooterLink[] }) {
  const t = useTranslate();
  const localise = useLocalisedHref();

  return (
    <ul className="flex flex-col gap-4 sm:gap-1">
      {links.map((link) => (
        <li key={link.labelKey}>
          <a
            href={localise(link.href)}
            {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="inline-flex items-center gap-1.5 text-sm font-normal text-brand-footerMuted no-underline transition-colors hover:text-white sm:text-xs"
          >
            {t(Texts[link.labelKey])}
            {link.external && <img src="/images/footer/icon-outbound.svg" alt="" className="size-3" />}
          </a>
        </li>
      ))}
    </ul>
  );
}

export function Footer() {
  const t = useTranslate();
  const contact = useFooterContact();
  const usefulInfoLinks = useFooterUsefulInfoLinks();

  return (
    <footer className="bg-brand-footer">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-8 px-8 py-10">
        <div className="flex flex-col gap-6 sm:grid sm:grid-cols-2 sm:items-start sm:gap-x-12 sm:gap-y-6 lg:flex lg:flex-row lg:items-start lg:gap-12">
          <div className="flex flex-col gap-6 sm:col-start-1 sm:row-start-1 lg:flex-row">
            {/* Desktop: the logo column is as wide as it takes for the divider after it to line up with the
             * catalog sidebar's right border (sidebar 228 + page padding 24 - footer padding 32 - gap 24). */}
            <div className="shrink-0 self-start lg:w-[196px]">
              <img
                src="/images/logo-footer-clean.png"
                alt={t(Texts.BrandName)}
                width={594}
                height={108}
                className="block h-6 w-auto lg:h-[30px]"
              />
            </div>

            <div className="hidden w-px bg-gray-450 lg:block" />

            <ul className="flex flex-col gap-3">
              {getQuickContacts(contact).map((quickContact) => (
                <li key={quickContact.href}>
                  <a
                    href={quickContact.href}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-white no-underline transition-colors hover:text-brand-footerMuted"
                  >
                    <img src={quickContact.icon} alt="" className="size-4" />
                    {quickContact.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-3 text-xs font-normal text-brand-footerMuted sm:col-start-1 sm:row-start-2">
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-white">{contact.legalName}</span>
              {contact.addressLines.map((line) => (
                <span key={line}>{line}</span>
              ))}
              <span>{t(Texts.FooterCountry)}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span>
                {t(Texts.FooterIdLabel)} {contact.ico}
              </span>
              <span>
                {t(Texts.FooterTinLabel)} {contact.dic}
              </span>
            </div>
          </div>

          <div className="sm:col-start-2 sm:row-start-1">
            <FooterAccordionSection title={t(Texts.FooterPartnersHeading)}>
              <FooterLinkList links={FOOTER_PARTNER_LINKS} />
            </FooterAccordionSection>
          </div>

          <div className="sm:col-start-2 sm:row-start-2">
            <FooterAccordionSection title={t(Texts.FooterUsefulInfoHeading)}>
              <FooterLinkList links={usefulInfoLinks} />
            </FooterAccordionSection>
          </div>
        </div>

        <div className="h-px bg-gray-450" />

        <p className="text-xs font-normal text-brand-footerMuted">{t(Texts.FooterCopyright)}</p>
      </div>
    </footer>
  );
}
